import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Select, AutoComplete, DatePicker, Button, Modal, Checkbox, Typography, Space, message } from "antd";
import { LeftOutlined, RightOutlined, PlusOutlined, CopyOutlined } from "@ant-design/icons";
import moment from "moment";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import {
  getActiveFacilities,
  getStaffListing,
  getAllShiftAssignments,
  addShiftAssignment,
  editShiftAssignment,
  deleteShiftAssignment,
  getAllSessions,
} from "./workpulseApi";

const { Option } = Select;
const { Text } = Typography;
const { confirm } = Modal;

const staffDisplayName = (item) => {
  const first = item?.first_name || item?.firstName || "";
  const last = item?.last_name || item?.lastName || "";
  return `${first} ${last}`.trim() || item?.full_name || item?.name || "Unknown";
};

const DAY_OPTIONS = [
  { label: "Mon", value: 0 }, { label: "Tue", value: 1 }, { label: "Wed", value: 2 }, { label: "Thu", value: 3 },
  { label: "Fri", value: 4 }, { label: "Sat", value: 5 }, { label: "Sun", value: 6 },
];

// The day checkboxes with a "Select All Days" box in front.
const DayPicker = ({ value, onChange, disabled }) => {
  const all = DAY_OPTIONS.map((d) => d.value);
  return (
    <Space wrap>
      <Checkbox
        checked={value.length === all.length}
        indeterminate={value.length > 0 && value.length < all.length}
        onChange={(e) => onChange(e.target.checked ? all : [])}
        disabled={disabled}
      >
        Select All Days
      </Checkbox>
      <Checkbox.Group options={DAY_OPTIONS} value={value} onChange={onChange} disabled={disabled} />
    </Space>
  );
};

// A plain 15-minute-step list — much quicker to pick from than scrolling a native time
// widget's hour/minute wheels. Whatever value is already set (even off-grid, e.g. an old
// 07:05 shift) is added in too, so editing an existing shift never shows a blank field.
const TIME_OPTIONS = (() => {
  const list = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      list.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return list;
})();
const timeSuggestions = (current) => {
  const list = current && !TIME_OPTIONS.includes(current) ? [current, ...TIME_OPTIONS] : TIME_OPTIONS;
  return list.map((t) => ({ value: t }));
};
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
// Typed entry like "7:05" is perfectly valid but doesn't match TIME_PATTERN's zero-padded
// form — this reformats it to "07:05" instead of rejecting it. Returns the input unchanged
// if it isn't a plausible time at all, so the caller's own validation still catches real errors.
const normalizeTime = (raw) => {
  if (!raw) return raw;
  const trimmed = raw.trim();
  let h, mi;

  const colonMatch = trimmed.match(/^(\d{1,2}):(\d{1,2})$/);
  if (colonMatch) {
    h = parseInt(colonMatch[1], 10);
    mi = parseInt(colonMatch[2], 10);
  } else if (/^\d{1,4}$/.test(trimmed)) {
    // Bare digits, no colon — "20" -> hour only (20:00); "930"/"1430" -> HMM/HHMM.
    if (trimmed.length <= 2) {
      h = parseInt(trimmed, 10);
      mi = 0;
    } else {
      h = parseInt(trimmed.slice(0, -2), 10);
      mi = parseInt(trimmed.slice(-2), 10);
    }
  } else {
    return raw;
  }

  if (h < 0 || h > 23 || mi < 0 || mi > 59) return raw;
  return `${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}`;
};

// A recurring/multi-day assignment edited from one cell still changes everywhere it
// applies — this tells the modal whether to show that warning.
const spansMultipleDays = (assignment) => {
  if (!assignment) return false;
  if (!assignment.effective_to || !moment(assignment.effective_to).isSame(moment(assignment.effective_from), "day")) return true;
  const days = assignment.days_of_week ? assignment.days_of_week.split(",") : null;
  return !!days && days.length > 1;
};

// Expands assignments into concrete per-day occurrences for one week, matching the same
// facility/date-range/days-of-week rule everywhere in this file (current week's grid, and
// the previous week when copying). occurrences[user_id][dayIndex] = { start_time, end_time }
const expandOccurrences = (assignmentsList, weekDaysList, facilityId) => {
  const map = {};
  assignmentsList
    .filter((a) => !facilityId || !a.facility_id || a.facility_id === facilityId)
    .forEach((a) => {
      const from = moment(a.effective_from);
      const to = a.effective_to ? moment(a.effective_to) : moment(weekDaysList[weekDaysList.length - 1]);
      const days = a.days_of_week ? a.days_of_week.split(",").map((v) => parseInt(v, 10)) : null;
      weekDaysList.forEach((d, dayIndex) => {
        if (d.isBefore(from, "day") || d.isAfter(to, "day")) return;
        if (days && !days.includes(d.isoWeekday() - 1)) return;
        if (!map[a.user_id]) map[a.user_id] = {};
        map[a.user_id][dayIndex] = { assignment: a, start_time: a.start_time, end_time: a.end_time };
      });
    });
  return map;
};

// ---- Assign, edit or remove one day's shift for one staff member ----
const ShiftCellModal = ({ visible, context, onClose, onSaved }) => {
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedDays, setSelectedDays] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible && context) {
      const { occurrence, date } = context;
      setStartTime(occurrence ? occurrence.start_time.slice(0, 5) : "");
      setEndTime(occurrence ? occurrence.end_time.slice(0, 5) : "");
      if (occurrence?.assignment?.days_of_week) {
        setSelectedDays(occurrence.assignment.days_of_week.split(",").map((v) => parseInt(v, 10)));
      } else if (occurrence) {
        setSelectedDays(DAY_OPTIONS.map((d) => d.value)); // null days_of_week = every day
      } else {
        setSelectedDays([date.isoWeekday() - 1]); // new shift — default to just the clicked day
      }
    }
  }, [visible, context]);

  if (!context) return null;
  const { userId, staffName, date, occurrence, facilityId, weekStart } = context;
  const recurring = spansMultipleDays(occurrence?.assignment);
  const clickedDay = date.isoWeekday() - 1;
  const isJustTheClickedDay = selectedDays.length === 1 && selectedDays[0] === clickedDay;
  // A multi-day pick from this quick modal applies to the checked days for the viewed week
  // only — not an ongoing pattern. Anchoring to the start of that week (rather than the exact
  // cell clicked) means every checked day in that same week is covered, not just the ones
  // on/after the clicked date. An actually ongoing schedule is a deliberate choice made in
  // Bulk Assign Roster instead, where "Effective To" is a visible field, not an implicit side
  // effect of checking more than one day here.
  const weekStartStr = moment(weekStart).format("YYYY-MM-DD");
  const weekEndStr = moment(weekStart).add(6, "days").format("YYYY-MM-DD");

  const handleSave = async () => {
    const normStart = normalizeTime(startTime);
    const normEnd = normalizeTime(endTime);
    if (!normStart || !normEnd) { message.error("Please select a start and end time."); return; }
    if (!TIME_PATTERN.test(normStart) || !TIME_PATTERN.test(normEnd)) { message.error("Please enter times as HH:mm, e.g. 07:05."); return; }
    if (selectedDays.length === 0) { message.error("Please select at least one day."); return; }
    try {
      setSaving(true);
      const start_time = normStart + ":00";
      const end_time = normEnd + ":00";
      const dateStr = date.format("YYYY-MM-DD");

      let error, result;
      if (occurrence) {
        // Preserve the existing rule's own facility/effective-date scope — only the time and
        // the days it applies to change here.
        const a = occurrence.assignment;
        ({ error, result } = await editShiftAssignment(a.id, {
          facility_id: a.facility_id || undefined,
          start_time,
          end_time,
          effective_from: a.effective_from,
          effective_to: a.effective_to || undefined,
          days_of_week: selectedDays,
        }));
      } else {
        // Only the clicked day checked -> a one-off shift just for this date. Any other/extra
        // day checked -> the same shift on those days for this viewed week only (not an
        // ongoing pattern — use Bulk Assign Roster for that, where it's an explicit choice).
        ({ error, result } = await addShiftAssignment({
          user_id: userId,
          facility_id: facilityId,
          start_time,
          end_time,
          effective_from: isJustTheClickedDay ? dateStr : weekStartStr,
          effective_to: isJustTheClickedDay ? dateStr : weekEndStr,
          days_of_week: selectedDays,
        }));
      }
      if (error) message.error(result?.message || "Failed to save shift.");
      else { message.success(occurrence ? "Shift updated." : "Shift assigned."); onSaved(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = () => {
    confirm({
      title: "Remove shift",
      content: recurring
        ? "This schedule repeats on other days or dates too — removing it removes all of them, not just this one day. Continue?"
        : "Remove this shift?",
      okText: "Remove",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deleteShiftAssignment(occurrence.assignment.id);
        if (error) message.error(result?.message || "Failed to remove shift.");
        else { message.success("Shift removed."); onSaved(); onClose(); }
      },
    });
  };

  return (
    <Modal
      title={`${staffName} · ${date.format("dddd")} ${date.format(DATE_FORMAT_DD_MM_YYYY)}`}
      visible={visible}
      onCancel={onClose}
      destroyOnClose
      footer={[
        occurrence && (
          <Button key="remove" danger onClick={handleRemove}>Remove</Button>
        ),
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>Save shift</Button>,
      ].filter(Boolean)}
    >
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Start Time</div>
          <AutoComplete
            value={startTime}
            placeholder="e.g. 07:00"
            disabled={saving}
            style={{ width: "100%" }}
            options={timeSuggestions(startTime)}
            filterOption={(input, option) => option.value.includes(input)}
            onChange={setStartTime}
            onBlur={() => setStartTime((v) => normalizeTime(v))}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 6, fontWeight: 500, fontSize: 13 }}>End Time</div>
          <AutoComplete
            value={endTime}
            placeholder="e.g. 15:00"
            disabled={saving}
            style={{ width: "100%" }}
            options={timeSuggestions(endTime)}
            filterOption={(input, option) => option.value.includes(input)}
            onChange={setEndTime}
            onBlur={() => setEndTime((v) => normalizeTime(v))}
          />
        </div>
      </div>

      <div style={{ marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Days</div>
      <DayPicker value={selectedDays} onChange={setSelectedDays} disabled={saving} />
      <div style={{ marginTop: 6 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {isJustTheClickedDay
            ? "Applies just to this one date."
            : `Applies to the checked days for this week only (${moment(weekStart).format(DATE_FORMAT_DD_MM_YYYY)} – ${moment(weekStart).add(6, "days").format(DATE_FORMAT_DD_MM_YYYY)}). For an ongoing schedule, use Bulk Assign Roster instead.`}
        </Text>
      </div>

      {recurring && (
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
          This shift is part of a recurring schedule. Changes here apply everywhere that schedule already applies.
        </Text>
      )}
    </Modal>
  );
};

// ---- Assign the same shift to many staff at once — for setting up a roster from scratch
// instead of clicking through every person's cell individually. ----
const BulkAssignModal = ({ visible, facilityId, allStaff, onClose, onSaved }) => {
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [saving, setSaving] = useState(false);
  // Always today's actual current week — independent of whatever week the Roster grid
  // happens to be scrolled to, since that can be navigated forward/back for review.
  const currentWeekStart = moment().startOf("isoWeek");

  useEffect(() => {
    if (visible) {
      setSelectedStaffIds([]);
      setSelectedDays([]);
      setStartTime("");
      setEndTime("");
    }
  }, [visible]);

  const handleSave = async () => {
    const normStart = normalizeTime(startTime);
    const normEnd = normalizeTime(endTime);
    if (selectedStaffIds.length === 0) { message.error("Please select at least one staff member."); return; }
    if (!normStart || !normEnd) { message.error("Please select a start and end time."); return; }
    if (!TIME_PATTERN.test(normStart) || !TIME_PATTERN.test(normEnd)) { message.error("Please enter times as HH:mm, e.g. 07:05."); return; }
    if (selectedDays.length === 0) { message.error("Please select at least one day."); return; }

    const effectiveFromStr = currentWeekStart.format("YYYY-MM-DD");
    const effectiveToStr = moment(currentWeekStart).add(6, "days").format("YYYY-MM-DD");

    setSaving(true);
    let created = 0, failed = 0;
    for (const userId of selectedStaffIds) {
      // eslint-disable-next-line no-await-in-loop
      const { error } = await addShiftAssignment({
        user_id: userId,
        facility_id: facilityId,
        start_time: normStart + ":00",
        end_time: normEnd + ":00",
        effective_from: effectiveFromStr,
        effective_to: effectiveToStr,
        days_of_week: selectedDays,
      });
      if (error) failed += 1; else created += 1;
    }
    setSaving(false);
    onSaved();
    if (failed === 0) { message.success(`Added shifts for ${created} staff member(s).`); onClose(); }
    else { message.warning(`Added for ${created}, failed for ${failed} (most likely an existing shift already overlaps for that person).`); }
  };

  return (
    <Modal
      title="Bulk Assign Roster"
      visible={visible}
      onCancel={onClose}
      destroyOnClose
      width={560}
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Assign{selectedStaffIds.length ? ` to ${selectedStaffIds.length} Staff` : ""}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Staff Members</div>
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="children"
          placeholder="Select everyone this shift applies to"
          style={{ width: "100%" }}
          value={selectedStaffIds}
          onChange={setSelectedStaffIds}
          disabled={saving}
          maxTagCount={6}
        >
          {allStaff.map((s) => <Option key={s.id ?? s.user_id} value={s.id ?? s.user_id}>{staffDisplayName(s)}</Option>)}
        </Select>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Start Time</div>
          <AutoComplete
            value={startTime}
            placeholder="e.g. 07:00"
            disabled={saving}
            style={{ width: "100%" }}
            options={timeSuggestions(startTime)}
            filterOption={(input, option) => option.value.includes(input)}
            onChange={setStartTime}
            onBlur={() => setStartTime((v) => normalizeTime(v))}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 6, fontWeight: 500, fontSize: 13 }}>End Time</div>
          <AutoComplete
            value={endTime}
            placeholder="e.g. 15:00"
            disabled={saving}
            style={{ width: "100%" }}
            options={timeSuggestions(endTime)}
            filterOption={(input, option) => option.value.includes(input)}
            onChange={setEndTime}
            onBlur={() => setEndTime((v) => normalizeTime(v))}
          />
        </div>
      </div>

      <div style={{ marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Days</div>
      <DayPicker value={selectedDays} onChange={setSelectedDays} disabled={saving} />

      <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 10 }}>
        Applies this same time to every selected staff member on the checked days, for the current week ({currentWeekStart.format(DATE_FORMAT_DD_MM_YYYY)} – {moment(currentWeekStart).add(6, "days").format(DATE_FORMAT_DD_MM_YYYY)}).
      </Text>
    </Modal>
  );
};

const Roster = () => {
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState(null);
  const [weekStart, setWeekStart] = useState(moment().startOf("isoWeek"));
  const [staff, setStaff] = useState([]);
  const [allClientStaff, setAllClientStaff] = useState([]);
  const [extraStaff, setExtraStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [cellContext, setCellContext] = useState(null);
  const [addingRow, setAddingRow] = useState(false);
  const [bulkAssignVisible, setBulkAssignVisible] = useState(false);

  const loadFacilities = useCallback(async () => {
    const { error, result } = await getActiveFacilities();
    const list = !error && Array.isArray(result?.result) ? result.result : [];
    setFacilities(list);
    setFacilityId((current) => current || list[0]?.facility_id || null);
  }, []);

  const loadAssignments = useCallback(async () => {
    const { error, result } = await getAllShiftAssignments();
    setAssignments(!error && Array.isArray(result?.result) ? result.result : []);
  }, []);

  const loadStaff = useCallback(async (facId) => {
    if (!facId) { setStaff([]); return; }
    const params = new URLSearchParams({ page: "1", limit: "200", facility_id: facId });
    const { error, result } = await getStaffListing(params);
    const payload = result?.result ?? result ?? {};
    setStaff(!error && Array.isArray(payload?.data) ? payload.data : []);
  }, []);

  // Unfiltered — every staff member linked to this client, for the inline "add row" picker so
  // it isn't limited to people already tied to this facility.
  const loadAllClientStaff = useCallback(async () => {
    const params = new URLSearchParams({ page: "1", limit: "500" });
    const { error, result } = await getStaffListing(params);
    const payload = result?.result ?? result ?? {};
    setAllClientStaff(!error && Array.isArray(payload?.data) ? payload.data : []);
  }, []);

  const loadSessions = useCallback(async (facId, week) => {
    if (!facId) { setSessions([]); return; }
    const params = new URLSearchParams({
      limit: "1000",
      facility_id: facId,
      from: week.format("YYYY-MM-DD 00:00:00"),
      to: moment(week).endOf("isoWeek").format("YYYY-MM-DD 23:59:59"),
    });
    const { error, result } = await getAllSessions(params);
    setSessions(!error && Array.isArray(result?.result) ? result.result : []);
  }, []);

  useEffect(() => { loadFacilities(); loadAssignments(); loadAllClientStaff(); }, [loadFacilities, loadAssignments, loadAllClientStaff]);
  useEffect(() => {
    setLoading(true);
    Promise.all([loadStaff(facilityId), loadSessions(facilityId, weekStart)]).finally(() => setLoading(false));
  }, [facilityId, weekStart, loadStaff, loadSessions]);
  // Manually-added-but-not-yet-assigned staff and the inline add-row are specific to one facility view.
  useEffect(() => { setExtraStaff([]); setAddingRow(false); }, [facilityId]);

  const weekDays = useMemo(() => [...Array(7)].map((_, i) => moment(weekStart).add(i, "day")), [weekStart]);

  const clockedInUserIds = useMemo(
    () => new Set(sessions.filter((s) => s.is_open === 1 || s.is_open === true).map((s) => s.user_id)),
    [sessions]
  );

  // occurrencesByUserAndDate[user_id]["YYYY-MM-DD"] = { assignment, start_time, end_time }
  const occurrencesByUserAndDate = useMemo(() => {
    const byIndex = expandOccurrences(assignments, weekDays, facilityId);
    const map = {};
    Object.keys(byIndex).forEach((userId) => {
      map[userId] = {};
      Object.keys(byIndex[userId]).forEach((dayIndex) => {
        map[userId][weekDays[dayIndex].format("YYYY-MM-DD")] = byIndex[userId][dayIndex];
      });
    });
    return map;
  }, [assignments, weekDays, facilityId]);

  // The grid's row list: staff already linked to this facility, plus anyone who already has
  // a shift here (even without a facility link, e.g. added inline then assigned), plus anyone
  // just added inline but not yet assigned — so they don't disappear before their first shift
  // is saved.
  const combinedStaff = useMemo(() => {
    const map = new Map();
    const addPerson = (p) => {
      const key = p.id ?? p.user_id;
      if (key == null || map.has(key)) return;
      map.set(key, p);
    };
    staff.forEach(addPerson);
    assignments
      .filter((a) => !facilityId || !a.facility_id || a.facility_id === facilityId)
      .forEach((a) => addPerson({ user_id: a.user_id, first_name: a.first_name, last_name: a.last_name }));
    extraStaff.forEach(addPerson);
    return Array.from(map.values());
  }, [staff, assignments, facilityId, extraStaff]);

  const combinedStaffIds = useMemo(() => new Set(combinedStaff.map((s) => s.id ?? s.user_id)), [combinedStaff]);
  const availableToAdd = useMemo(() => allClientStaff.filter((s) => !combinedStaffIds.has(s.id ?? s.user_id)), [allClientStaff, combinedStaffIds]);

  const openCell = (staffMember, date) => {
    const userId = staffMember.id ?? staffMember.user_id;
    const dateKey = date.format("YYYY-MM-DD");
    const occurrence = (occurrencesByUserAndDate[userId] || {})[dateKey] || null;
    setCellContext({ userId, staffName: staffDisplayName(staffMember), date, occurrence, facilityId, weekStart });
  };

  const handleAddRowSelect = (userId) => {
    const person = allClientStaff.find((s) => (s.id ?? s.user_id) === userId);
    if (person) setExtraStaff((prev) => [...prev, person]);
    setAddingRow(false);
  };

  const handleCopyPreviousWeek = () => {
    const prevWeekDays = weekDays.map((d) => moment(d).subtract(1, "week"));
    const prevOccurrences = expandOccurrences(assignments, prevWeekDays, facilityId);
    var toCopy = [];
    Object.keys(prevOccurrences).forEach((userId) => {
      Object.keys(prevOccurrences[userId]).forEach((dayIndex) => {
        toCopy.push({ user_id: parseInt(userId, 10), dayIndex: parseInt(dayIndex, 10), occurrence: prevOccurrences[userId][dayIndex] });
      });
    });

    if (toCopy.length === 0) {
      message.info("No shifts found in the previous week to copy.");
      return;
    }

    copyShifts(toCopy);
  };

  const copyShifts = async (toCopy) => {
    setCopying(true);
    let created = 0, skipped = 0, failed = 0;
    for (const item of toCopy) {
      const targetDate = weekDays[item.dayIndex];
      const dateKey = targetDate.format("YYYY-MM-DD");
      const alreadyExists = !!(occurrencesByUserAndDate[item.user_id] || {})[dateKey];
      if (alreadyExists) { skipped += 1; continue; }
      // eslint-disable-next-line no-await-in-loop
      const { error } = await addShiftAssignment({
        user_id: item.user_id,
        facility_id: facilityId,
        start_time: item.occurrence.start_time,
        end_time: item.occurrence.end_time,
        effective_from: dateKey,
        effective_to: dateKey,
        days_of_week: [targetDate.isoWeekday() - 1],
      });
      if (error) failed += 1; else created += 1;
    }
    setCopying(false);
    loadAssignments();
    message.success(
      `Copied ${created} shift(s) from last week` +
      (skipped ? `, skipped ${skipped} (already scheduled)` : "") +
      (failed ? `, ${failed} failed` : "") + "."
    );
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <Select placeholder="Select a facility" style={{ width: 240 }} value={facilityId || undefined} onChange={setFacilityId}>
            {facilities.map((f) => <Option key={f.facility_id} value={f.facility_id}>{f.facility_name}</Option>)}
          </Select>
          <Space size="small">
            <Button icon={<LeftOutlined />} onClick={() => setWeekStart((w) => moment(w).subtract(1, "week"))} />
            <DatePicker
              picker="week"
              allowClear={false}
              value={weekStart}
              onChange={(d) => d && setWeekStart(moment(d).startOf("isoWeek"))}
              format={(value) => `Week of ${moment(value).startOf("isoWeek").format(DATE_FORMAT_DD_MM_YYYY)}`}
            />
            <Button icon={<RightOutlined />} onClick={() => setWeekStart((w) => moment(w).add(1, "week"))} />
            <Button onClick={() => setWeekStart(moment().startOf("isoWeek"))}>Today</Button>
          </Space>
        </div>
        <Space size="small">
          <Button icon={<PlusOutlined />} type="primary" disabled={!facilityId} onClick={() => setBulkAssignVisible(true)}>
            Bulk Assign Roster
          </Button>
          <Button icon={<CopyOutlined />} loading={copying} disabled={!facilityId} onClick={handleCopyPreviousWeek}>
            Copy Previous Week
          </Button>
        </Space>
      </div>

      {!facilityId ? (
        <Text type="secondary">Select a facility to view its roster.</Text>
      ) : (
        <div style={{ border: "1px solid #E6E9EE", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "200px repeat(7, 1fr)" }}>
            <div style={{ background: "#F6F7F9", borderBottom: "1px solid #E6E9EE" }} />
            {weekDays.map((d) => (
              <div
                key={d.format("YYYY-MM-DD")}
                style={{ background: "#F6F7F9", padding: "10px 12px", fontWeight: 600, fontSize: 12, color: "#101828", borderBottom: "1px solid #E6E9EE", borderLeft: "1px solid #E6E9EE", textAlign: "center" }}
              >
                {d.format("ddd")} <span style={{ color: "#5B6472", fontWeight: 400 }}>{d.format(DATE_FORMAT_DD_MM_YYYY)}</span>
              </div>
            ))}

            {combinedStaff.map((s) => {
              const userId = s.id ?? s.user_id;
              return (
                <React.Fragment key={userId}>
                  <div style={{ padding: "10px 12px", borderBottom: "1px solid #E6E9EE", display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 13 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: clockedInUserIds.has(userId) ? "#1A8F5C" : "#D0D4D7", flexShrink: 0 }} />
                    {staffDisplayName(s)}
                  </div>
                  {weekDays.map((d) => {
                    const dateKey = d.format("YYYY-MM-DD");
                    const occ = (occurrencesByUserAndDate[userId] || {})[dateKey];
                    return (
                      <div
                        key={dateKey}
                        onClick={() => openCell(s, d)}
                        style={{ padding: 8, borderBottom: "1px solid #E6E9EE", borderLeft: "1px solid #E6E9EE", cursor: "pointer", minHeight: 52, display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        {occ ? (
                          <div style={{ background: "#EAF1FD", color: "#2F6FE0", borderRadius: 6, padding: "5px 7px", fontSize: 11.5, fontWeight: 700, textAlign: "center", width: "100%" }}>
                            {occ.start_time.slice(0, 5)}–{occ.end_time.slice(0, 5)}
                          </div>
                        ) : (
                          <span style={{ color: "#D0D4D7", fontSize: 16 }}>+</span>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* Inline last row — pick a staff member directly here instead of a modal. */}
            <div style={{ padding: "6px 12px", display: "flex", alignItems: "center", minHeight: 44 }}>
              {addingRow ? (
                <Select
                  autoFocus
                  showSearch
                  optionFilterProp="children"
                  placeholder="Select an existing staff member"
                  style={{ width: "100%" }}
                  onChange={handleAddRowSelect}
                  onBlur={() => setAddingRow(false)}
                  notFoundContent={availableToAdd.length === 0 ? "Everyone from this client is already shown" : undefined}
                >
                  {availableToAdd.map((s) => <Option key={s.id ?? s.user_id} value={s.id ?? s.user_id}>{staffDisplayName(s)}</Option>)}
                </Select>
              ) : (
                <Button type="dashed" size="small" icon={<PlusOutlined />} style={{ width: "100%" }} onClick={() => setAddingRow(true)}>
                  Add to Roster
                </Button>
              )}
            </div>
            {weekDays.map((d) => (
              <div key={`new-${d.format("YYYY-MM-DD")}`} style={{ minHeight: 44 }} />
            ))}

            {combinedStaff.length === 0 && !loading && (
              <div style={{ gridColumn: "1 / -1", padding: "0 24px 20px", textAlign: "center", color: "#5B6472" }}>
                No staff found for this facility. Use "Add to Roster" below to bring someone in.
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 12, color: "#5B6472", alignItems: "center" }}>
        <span><span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#1A8F5C", marginRight: 6 }} />Clocked in now</span>
        <span><span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#D0D4D7", marginRight: 6 }} />Not clocked in</span>
      </div>

      <ShiftCellModal
        visible={!!cellContext}
        context={cellContext}
        onClose={() => setCellContext(null)}
        onSaved={loadAssignments}
      />
      <BulkAssignModal
        visible={bulkAssignVisible}
        facilityId={facilityId}
        allStaff={allClientStaff}
        onClose={() => setBulkAssignVisible(false)}
        onSaved={loadAssignments}
      />
    </div>
  );
};

export default memo(Roster);
